// Cliente de la API.
// - El access token vive SOLO en memoria (nunca en localStorage): un script inyectado no lo encuentra guardado.
// - La sesión se renueva con la cookie httpOnly del refresh token, que el JavaScript no puede leer.
// - Ante un 401, renueva una sola vez (aunque varias peticiones fallen a la vez) y reintenta.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Código de máquina de la API, ej. "mfa_step_up_required" o "permission_denied". */
    readonly code?: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let sessionExpired: () => void = () => {};

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

/** Lo registra el proveedor de sesión: se llama cuando la sesión ya no se puede renovar. */
export function onSessionExpired(listener: () => void): void {
  sessionExpired = listener;
}

/** Renueva el access token con la cookie de sesión. Si hay una renovación en curso, la reutiliza. */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin', headers: { accept: 'application/json' } });
      if (!res.ok) {
        accessToken = null;
        return false;
      }
      accessToken = ((await res.json()) as { accessToken: string }).accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Rutas que no usan el access token: su 401 significa credenciales inválidas, no sesión vencida. */
const PUBLIC_AUTH = new Set(['/api/auth/login', '/api/auth/refresh', '/api/auth/logout', '/api/auth/2fa/verify']);

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Cuerpo JSON. */
  json?: unknown;
  /** Cuerpo sin procesar (ej. FormData para adjuntos). */
  body?: BodyInit;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await request(path, opts);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Descarga un archivo protegido (los adjuntos piden el token, así que un enlace simple no sirve). */
export async function downloadFile(path: string, fileName: string): Promise<void> {
  const blob = await (await request(path)).blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Envía la petición con el token; ante un 401 renueva la sesión una vez y reintenta. */
async function request(path: string, opts: RequestOptions = {}): Promise<Response> {
  const send = () =>
    fetch(path, {
      method: opts.method ?? (opts.json !== undefined || opts.body !== undefined ? 'POST' : 'GET'),
      credentials: 'same-origin',
      signal: opts.signal,
      headers: {
        accept: 'application/json',
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...(opts.json !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: opts.json !== undefined ? JSON.stringify(opts.json) : opts.body,
    });

  let res = await send();
  if (res.status === 401 && !PUBLIC_AUTH.has(path.split('?')[0]!)) {
    if (await refreshSession()) {
      res = await send();
    } else {
      sessionExpired();
    }
  }
  if (!res.ok) throw await toApiError(res);
  return res;
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  const b = (body ?? {}) as { message?: unknown; code?: unknown };
  const message = Array.isArray(b.message) ? b.message.join('. ') : typeof b.message === 'string' ? b.message : `Error ${res.status}`;
  return new ApiError(res.status, message, typeof b.code === 'string' ? b.code : undefined, body);
}
